import type { AddonReservation, SelfHotelReservation } from 'niara-spear-crudmodel'
import type { AddonAvailabilityResult } from '../types/addon'
import type { RoomRate } from '../types/hotels'

type RecurringValues = {
  value: number
  valueWithoutDiscount: number
  currency: string
  addonValue: number
  addonCurrency: string
  discountValue: number
  totalValue: number
  totalValueWithoutDiscount: number
  isRecurringPayments: boolean
}

/**
 * Pega a partir do RoomRate os valores por valor recorrente (long stay) ou o valor total (short stay)
 * @param roomRate
 * @returns Objeto contendo:
 *  - value: Valor total, se for shortStay, ou valor recorrente se for longStay
 *  - valueWithoutDiscount: Valor sem desconto
 *  - currency: Moeda do valor recorrente
 *  - addonValue: Valor total dos addons (serviços extras)
 *  - addonCurrency: Moeda dos addons
 *  - discountValue: Valor do desconto aplicado
 *  - totalValue: Valor total incluindo addons
 *  - totalValueWithoutDiscount: Valor total incluindo addons sem desconto
 *  - isRecurringPayments: Indica se o pagamento é recorrente (true para long stay)
 */
export const getRecurringValues = (roomRate: Partial<RoomRate>): RecurringValues => {
  const priceComposition = roomRate?.priceComposition
  const recurringPayments = priceComposition?.recurringPayments
  const isRecurringPayments = recurringPayments?.length > 1
  const value = isRecurringPayments ? recurringPayments?.[0]?.value : priceComposition?.total?.value
  const currency = isRecurringPayments ? recurringPayments?.[0]?.currency : priceComposition?.total?.currency
  const discountValue = isRecurringPayments
    ? recurringPayments?.[0]?.discountValue || 0
    : priceComposition?.discountBreakdown?.reduce((acc, discount) => acc + (discount?.value || 0), 0) || 0
  const valueWithoutDiscount = value + discountValue

  const { addonValue, addonCurrency } = roomRate?.addons?.reduce(
    (acc, addon) => {
      const { addonValue, addonCurrency } = getAddonRecurringValues(addon)
      acc.addonValue += addonValue
      acc.addonCurrency = addonCurrency
      return acc
    },
    { addonValue: 0, addonCurrency: '' }
  ) || { addonValue: 0, addonCurrency: '' }

  const totalValue = value + addonValue
  const totalValueWithoutDiscount = valueWithoutDiscount + addonValue
  return {
    value,
    valueWithoutDiscount,
    currency,
    addonValue,
    addonCurrency,
    isRecurringPayments,
    totalValue,
    totalValueWithoutDiscount,
    discountValue,
  }
}

type AddonRecurringValues = {
  addonValue: number
  addonCurrency: string
  isRecurringPayment: boolean
}

/**
 * Pega os valores recorrentes de um addon, se long stay, ou o valor total, se short stay
 * @param addon
 * @returns Objeto contendo:
 * - addonValue: Valor total do addon (serviços extras), se for short stay, ou valor recorrente do addon se for longStay
 * - addonCurrency: Moeda do addon
 * - isRecurringPayment: Indica se o pagamento é recorrente (true para long stay)
 */
export const getAddonRecurringValues = (addon: AddonAvailabilityResult): AddonRecurringValues => {
  // TODO - Considera o recurring payments do primeiro paymentOption do addon
  const addonRecurringPayments = addon?.paymentOptions?.[0]?.recurringPayments
  const isRecurringPayment = addonRecurringPayments?.length > 1
  const addonValue = isRecurringPayment ? addonRecurringPayments?.[0]?.value : addon?.priceComposition?.total?.value
  const addonCurrency = isRecurringPayment
    ? addonRecurringPayments?.[0]?.currency
    : addon?.priceComposition?.total?.currency

  return { addonValue, addonCurrency, isRecurringPayment }
}

type ReservationRecurringValues = {
  value: number
  valueWithoutDiscount: number
  currency: string
  isRecurringPayment: boolean
}

/**
 * Pega os valores recorrentes de uma reserva, se long stay, ou o valor total, se short stay
 * @param roomStay
 * @returns Objeto contendo:
 * - value: Valor total da reserva, se for shortStay, ou valor recorrente se for longStay
 * - valueWithoutDiscount: Valor total da reserva sem desconto
 * - currency: Moeda do valor recorrente
 * - isRecurringPayment: Indica se o pagamento é recorrente (true para long stay)
 */
export const getReservationRecurringValues = (
  roomStay: SelfHotelReservation['roomStays'][number]
): ReservationRecurringValues => {
  const price = roomStay?.price
  const recurringPayment = price?.recurringPayment
  const isRecurringPayment = recurringPayment ? true : false
  const value = recurringPayment ? recurringPayment?.value : price?.total?.value
  const currency = recurringPayment ? recurringPayment?.currency : price?.total?.currency
  const discountValue = price?.discountBreakdown?.reduce((acc, discount) => acc + (discount?.value || 0), 0) || 0
  const valueWithoutDiscount = value + discountValue

  return { value, valueWithoutDiscount, currency, isRecurringPayment }
}

/**
 * Pega os valores recorrentes de um addon de uma reserva, se long stay, ou o valor total, se short stay
 * @param addon
 * @returns Objeto contendo:
 * - addonValue: Valor total do addon (serviços extras), se for short stay, ou valor recorrente do addon se for longStay
 * - addonCurrency: Moeda do addon
 * - isRecurringPayment: Indica se o pagamento é recorrente (true para long stay)
 */
export const getAddonReservationRecurringValues = (addon: AddonReservation): AddonRecurringValues => {
  const price = addon?.price
  const addonRecurringPayment = price?.recurringPayment
  const isRecurringPayment = addonRecurringPayment ? true : false
  const addonValue = addonRecurringPayment ? addonRecurringPayment?.value : price?.total?.value
  const addonCurrency = addonRecurringPayment ? addonRecurringPayment?.currency : price?.total?.currency
  return { addonValue, addonCurrency, isRecurringPayment }
}
